import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { toggleAchievementLike } from '../../../hooks/useNotifications';
import AchievementForm from './AchievementForm';

interface Achievement {
  id: string;
  user_id: string;
  title: string;
  description: string | null;
  achievement_date: string;
  category: string;
  image_url: string | null;
  created_at: string;
  visibility: 'public' | 'group_only';
  group_id: string | null;
  user_profiles?: {
    username: string;
    avatar_url: string | null;
  };
  likeCount?: number;
  isLiked?: boolean;
}

interface AchievementListProps {
  groupId?: string;
  limit?: number;
  refreshKey?: number;
}

const categories = [
  '全て',
  '資格試験',
  '学位取得',
  '論文発表',
  '研究成果',
  '学会発表',
  'コンペティション',
  '参考書読破',
  '過去問演習結果',
  'その他'
];

export default function AchievementList({ groupId, limit, refreshKey }: AchievementListProps) {
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('全て');
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);
  const [likingIds, setLikingIds] = useState<Set<string>>(new Set());

  const fetchCurrentUser = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
    return user?.id || null;
  }, []);

  const fetchAchievements = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const uid = user?.id || null;
      setCurrentUserId(uid);

      let query = supabase
        .from('achievements')
        .select('*')
        .order('achievement_date', { ascending: false });

      if (groupId) {
        const { data: members } = await supabase
          .from('study_group_members')
          .select('user_id')
          .eq('group_id', groupId);

        const memberIds = members?.map(m => m.user_id) || [];
        
        if (memberIds.length > 0) {
          query = query.or(`user_id.in.(${memberIds.join(',')}),group_id.eq.${groupId}`);
        } else {
          query = query.eq('group_id', groupId);
        }
      } else {
        query = query.eq('visibility', 'public');
      }

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;
      if (error) throw error;

      const items = data || [];

      if (items.length === 0) {
        setAchievements([]);
        return;
      }

      const userIds = [...new Set(items.map((a: Achievement) => a.user_id))];
      const achievementIds = items.map((a: Achievement) => a.id);

      const [profilesResult, likesCountResult, myLikesResult] = await Promise.all([
        supabase.from('user_profiles').select('user_id, username, avatar_url').in('user_id', userIds),
        supabase.from('achievement_likes').select('achievement_id').in('achievement_id', achievementIds),
        uid
          ? supabase.from('achievement_likes').select('achievement_id').in('achievement_id', achievementIds).eq('user_id', uid)
          : Promise.resolve({ data: [] }),
      ]);

      const profileMap = new Map((profilesResult.data || []).map((p: { user_id: string; username: string; avatar_url: string | null }) => [p.user_id, p]));
      
      const likeCountMap: Record<string, number> = {};
      (likesCountResult.data || []).forEach((l: { achievement_id: string }) => {
        likeCountMap[l.achievement_id] = (likeCountMap[l.achievement_id] || 0) + 1;
      });

      const myLikedSet = new Set((myLikesResult.data || []).map((l: { achievement_id: string }) => l.achievement_id));

      const merged = items.map((a: Achievement) => ({
        ...a,
        user_profiles: profileMap.get(a.user_id) || null,
        likeCount: likeCountMap[a.id] || 0,
        isLiked: myLikedSet.has(a.id),
      }));
      setAchievements(merged);
    } catch (err) {
      console.error('達成報告の取得エラー:', err);
    } finally {
      setLoading(false);
    }
  }, [groupId, limit]);

  useEffect(() => {
    fetchAchievements();
    
    const channel = supabase
      .channel('achievements_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'achievements' }, () => { fetchAchievements(); })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'achievement_likes' }, () => { fetchAchievements(); })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchAchievements]);

  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0) {
      fetchAchievements();
    }
  }, [refreshKey, fetchAchievements]);

  const handleLike = async (achievement: Achievement) => {
    if (!currentUserId || likingIds.has(achievement.id)) return;
    setLikingIds(prev => new Set(prev).add(achievement.id));
    
    // Optimistic update
    setAchievements(prev => prev.map(a => {
      if (a.id !== achievement.id) return a;
      return {
        ...a,
        isLiked: !a.isLiked,
        likeCount: (a.likeCount || 0) + (a.isLiked ? -1 : 1),
      };
    }));

    try {
      await toggleAchievementLike(
        achievement.id,
        achievement.title,
        achievement.user_id,
        currentUserId
      );
    } catch (err) {
      // Revert on error
      setAchievements(prev => prev.map(a => {
        if (a.id !== achievement.id) return a;
        return {
          ...a,
          isLiked: !a.isLiked,
          likeCount: (a.likeCount || 0) + (a.isLiked ? -1 : 1),
        };
      }));
    } finally {
      setLikingIds(prev => {
        const next = new Set(prev);
        next.delete(achievement.id);
        return next;
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('この達成報告を削除しますか？')) return;
    try {
      const { error } = await supabase.from('achievements').delete().eq('id', id);
      if (error) throw error;
      setAchievements(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('削除エラー:', err);
      alert('削除に失敗しました');
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const filteredAchievements = useMemo(() => {
    if (selectedCategory === '全て') return achievements;
    return achievements.filter(a => a.category === selectedCategory);
  }, [achievements, selectedCategory]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4 sm:space-y-5">
        {!limit && (
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`min-h-[36px] px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  selectedCategory === cat
                    ? 'bg-teal-600 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/15 hover:text-white'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}

        {filteredAchievements.length === 0 ? (
          <div className="text-center py-12 text-white/40">
            <i className="ri-trophy-line text-4xl sm:text-5xl mb-2"></i>
            <p className="text-sm sm:text-base">まだ達成報告がありません</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {filteredAchievements.map((achievement) => (
              <div
                key={achievement.id}
                className="bg-white/5 border border-white/10 rounded-xl hover:bg-white/8 hover:border-emerald-500/30 transition-all overflow-hidden"
              >
                {achievement.image_url && (
                  <div className="w-full h-36 sm:h-40 bg-white/5">
                    <img
                      src={achievement.image_url}
                      alt={achievement.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
                
                <div className="p-3 sm:p-4">
                  <div className="flex items-start justify-between mb-3 gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-sm font-bold shrink-0 overflow-hidden">
                        {achievement.user_profiles?.avatar_url ? (
                          <img
                            src={achievement.user_profiles.avatar_url}
                            alt={achievement.user_profiles.username}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          achievement.user_profiles?.username?.[0]?.toUpperCase() || 'U'
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs sm:text-sm font-medium text-white truncate">
                          {achievement.user_profiles?.username || 'Unknown'}
                        </p>
                        <p className="text-[10px] sm:text-xs text-white/40">
                          {new Date(achievement.achievement_date).toLocaleDateString('ja-JP')}
                        </p>
                      </div>
                    </div>
                    
                    {currentUserId === achievement.user_id && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => setEditingAchievement(achievement)}
                          className="min-w-[32px] min-h-[32px] flex items-center justify-center text-teal-400 hover:text-teal-300 hover:bg-teal-500/10 rounded-lg transition-colors cursor-pointer"
                          title="編集"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(achievement.id)}
                          className="min-w-[32px] min-h-[32px] flex items-center justify-center text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors cursor-pointer"
                          title="削除"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="inline-block px-2 py-0.5 bg-teal-500/20 text-teal-300 text-[10px] sm:text-xs rounded-full whitespace-nowrap border border-teal-500/20">
                      {achievement.category}
                    </span>
                    {achievement.visibility === 'group_only' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-[10px] sm:text-xs rounded-full whitespace-nowrap border border-emerald-500/20">
                        <i className="ri-group-line text-[10px]"></i>
                        グループ限定
                      </span>
                    )}
                  </div>

                  <h3 className="text-sm sm:text-base font-bold text-white mb-2 break-words line-clamp-2">
                    {achievement.title}
                  </h3>

                  {achievement.description && (
                    <div>
                      <p className={`text-xs sm:text-sm text-white/60 break-words ${expandedIds.has(achievement.id) ? '' : 'line-clamp-3'}`}>
                        {achievement.description}
                      </p>
                      {achievement.description.length > 80 && (
                        <button
                          onClick={() => toggleExpand(achievement.id)}
                          className="mt-1 text-[10px] sm:text-xs text-teal-400 hover:text-teal-300 font-medium cursor-pointer"
                        >
                          {expandedIds.has(achievement.id) ? '閉じる' : '続きを読む'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* いいねボタン */}
                  <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                    <button
                      onClick={() => handleLike(achievement)}
                      disabled={!currentUserId || likingIds.has(achievement.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        achievement.isLiked
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                          : 'bg-white/5 text-white/50 border border-white/10 hover:bg-white/10 hover:text-white/70'
                      } disabled:opacity-40 disabled:cursor-not-allowed`}
                    >
                      <i className={`${achievement.isLiked ? 'ri-heart-fill' : 'ri-heart-line'} text-sm`}></i>
                      <span>{achievement.likeCount || 0}</span>
                    </button>
                    <span className="text-[10px] text-white/30">
                      {new Date(achievement.created_at).toLocaleDateString('ja-JP')} 投稿
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingAchievement && (
        <AchievementForm
          editData={{
            id: editingAchievement.id,
            title: editingAchievement.title,
            description: editingAchievement.description,
            achievement_date: editingAchievement.achievement_date,
            category: editingAchievement.category,
            image_url: editingAchievement.image_url,
            visibility: editingAchievement.visibility,
            group_id: editingAchievement.group_id,
          }}
          onSuccess={() => {
            setEditingAchievement(null);
            fetchAchievements();
          }}
          onCancel={() => setEditingAchievement(null)}
        />
      )}
    </>
  );
}
